import { supabase } from "@/integrations/supabase/client";
import { getAttribution, type AttributionParams } from "@/lib/attribution";
import { effectiveConsent } from "@/lib/consent";

export type LandingFormValues = {
  name: string;
  email: string;
  phone: string;
  companyName?: string;
};
type CalAttribution = AttributionParams & { submissionId: string };

export function createSubmissionId(): string {
  const cryptoApi = globalThis.crypto;
  if (typeof cryptoApi?.randomUUID === "function") {
    try {
      return cryptoApi.randomUUID();
    } catch {
      /* Fall back for restricted contexts. */
    }
  }
  const bytes = new Uint8Array(16);
  let filled = false;
  if (typeof cryptoApi?.getRandomValues === "function") {
    try {
      cryptoApi.getRandomValues(bytes);
      filled = true;
    } catch {
      /* Use the final compatibility fallback. */
    }
  }
  if (!filled) {
    for (let index = 0; index < bytes.length; index++)
      bytes[index] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function buildCalBookingUrl(
  values: LandingFormValues,
  attribution: CalAttribution,
  opts: { leadSource?: string; calService?: string } = {},
) {
  const url = new URL("https://cal.com/team/smartly/discovery");
  url.searchParams.set("overlayCalendar", "true");
  url.searchParams.set("name", values.name);
  url.searchParams.set("email", values.email);
  url.searchParams.set("smsReminderNumber", values.phone);
  if (values.companyName)
    url.searchParams.set("companyName", values.companyName);
  url.searchParams.set("services", opts.calService ?? "web-development");
  for (const key of [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
  ] as const) {
    if (attribution[key]) url.searchParams.set(key, attribution[key] as string);
  }
  url.searchParams.set("metadata[submissionId]", attribution.submissionId);
  url.searchParams.set("metadata[lead_source]", opts.leadSource ?? LANDING_LEAD_SOURCE);
  for (const key of ["gclid", "gbraid", "wbraid"] as const) {
    if (attribution[key])
      url.searchParams.set(`metadata[${key}]`, attribution[key] as string);
  }
  url.searchParams.set("embed", "true");
  return url.toString();
}

export const LANDING_LEAD_SOURCE = "lp-web-development-san-diego";

export function buildAttributionColumns(leadSource: string = LANDING_LEAD_SOURCE) {
  const columns: Record<string, unknown> = { lead_source: leadSource };
  const attribution = getAttribution();
  if (!attribution) return columns;
  for (const key of [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
    "gclid",
    "gbraid",
    "wbraid",
  ] as const) {
    const value = attribution.last.params[key];
    if (value) columns[key] = value;
  }
  if (attribution.last.landing_path)
    columns.landing_page = attribution.last.landing_path;
  if (attribution.last.referrer) columns.referrer = attribution.last.referrer;
  const consent = effectiveConsent();
  columns.attribution = {
    first: attribution.first,
    last: attribution.last,
    consent: consent.ccpa_opt_out
      ? "opted_out"
      : consent.analytics
        ? "granted"
        : "undecided",
  };
  return columns;
}

type InsertResult = { error: { code?: string } | null };
const TIMED_OUT = Symbol("timed out");
const isSaved = (r: InsertResult) => !r.error || r.error.code === "23505";

function sendInsert(row: Record<string, unknown>): Promise<InsertResult> {
  // The query builder is a lazy thenable: wrap it once so it is sent exactly once.
  return Promise.resolve(
    supabase.from("consultation_submissions").insert(row as never),
  ).then(
    (r) => r as InsertResult,
    (error) => ({ error: { code: undefined, ...(error ?? {}) } }),
  );
}

function within(promise: Promise<InsertResult>, ms: number) {
  return Promise.race([
    promise,
    new Promise<typeof TIMED_OUT>((resolve) =>
      setTimeout(() => resolve(TIMED_OUT), ms),
    ),
  ]);
}

export async function saveLandingSubmission(
  submissionId: string,
  values: LandingFormValues,
  geo: { city: string | null; ip: string | null },
  opts: { leadSource?: string; services?: string[] } = {},
) {
  const leadSource = opts.leadSource ?? LANDING_LEAD_SOURCE;
  const base = {
    id: submissionId,
    name: values.name,
    email: values.email,
    company_name: values.companyName?.trim() || null,
    phone: values.phone,
    services: opts.services ?? ["Web Development"],
    city: geo.city,
    ip_address: geo.ip,
  };
  let extra: Record<string, unknown> = {};
  try {
    extra = buildAttributionColumns(leadSource);
  } catch {
    extra = { lead_source: leadSource };
  }
  let notified = false;
  const notify = () => {
    if (notified) return;
    notified = true;
    void Promise.resolve(
      supabase.functions.invoke("notify-consultation-submission", {
        body: { submissionId },
      }),
    ).catch(() => undefined);
  };
  const notifyWhenLate = (pending: Promise<InsertResult>) => {
    void pending.then((r) => {
      if (isSaved(r)) notify();
    });
  };

  const first = sendInsert({ ...base, ...extra });
  const firstResult = await within(first, 4000);
  if (firstResult === TIMED_OUT) {
    notifyWhenLate(first);
    return false;
  }
  if (isSaved(firstResult)) {
    notify();
    return true;
  }
  console.warn("Landing submission failed, retrying without attribution", firstResult.error);
  const retry = sendInsert(base);
  const retryResult = await within(retry, 3000);
  if (retryResult === TIMED_OUT) {
    notifyWhenLate(retry);
    return false;
  }
  if (isSaved(retryResult)) {
    notify();
    return true;
  }
  console.warn("Landing consultation submission failed", retryResult.error);
  return false;
}
